// Typed URL search params for the task list, `/sales/tasks` (spec 09 §4.12,
// spec 16 §3.12, D-313, D-314; P3-E2 PR E2-1).
//
// THE LIST KEEPS ITS FILTERS IN THE ADDRESS and adopts saved views as
// `sales.tasks`, with My day and Overdue as system views. Until E2-1 the
// Mine/All and Open/Done switches were component state and the address held
// `customer` alone; a saved view is a snapshot of URL state, so a filter kept
// anywhere else is one no view can store (the PR-E4 argument, D-305 rulings 3
// and 4). The URL keys, each with the parameter of `GET /v1/sales-activities`
// it becomes:
//
//   scope     scope              `all` only; absent is the reader's own tasks
//   status    status             `done` or `cancelled`; absent is open
//   due       due                overdue, today, through_today, upcoming
//   customer  customer_id
//   assignee  assignee_user_id   its control is shown to a literal admin only
//   order     order              worklist (declared), due_on, -due_on,
//                                -updated_at
//   view      the saved view the address names
//   peek      the side peek, `<kind>:<uuid>`; never a filter (D-314 ruling 8)
//
// A view stores `scope` only as `all` and `status` only as `done` or
// `cancelled`, so a view that does not say `all` reads its reader's own tasks
// and My day is each reader's own day. The address holds the same values and
// no others: `scope=mine` and `status=open` read as absent, so two equal views
// never compare unequal. The list has no kind filter, and `quote_id`,
// `sales_order_id` and `opportunity_id` are not in the address, because the
// composer, the order detail and the deal page read them for their own task
// lines and the list offers no control for them.
//
// The `?customer=<uuid>` a link sets keeps landing unchanged: it was the one
// parameter before E2-1, and it is the same key now.
//
// Defensive, as every sales validator is: a malformed value is dropped, never
// thrown on, and a default is an absent parameter.

import type { components } from "@assessly/api-client";
import { peekSearchParam } from "../../../../components/peek-address";
import { viewIdParam } from "../../views/lib/screens";
import { oneOf, uuidParam, type FilterKeyPin } from "../lib/search-grammar";

/** The order tokens of `GET /v1/sales-activities`. */
export type SalesTasksOrderToken = components["schemas"]["SalesActivitiesOrder"];

/** PR-E2's status-dependent order as one token (D-313 ruling 4): open tasks
 * by due date, closed ones newest first. */
export const TASKS_DEFAULT_ORDER: SalesTasksOrderToken = "worklist";

const TASKS_ORDER_SET: Record<SalesTasksOrderToken, true> = {
  worklist: true,
  due_on: true,
  "-due_on": true,
  "-updated_at": true,
};

/** Every order token the route takes. */
export const TASKS_ORDER_TOKENS = Object.keys(TASKS_ORDER_SET) as readonly SalesTasksOrderToken[];

export function isTasksOrderToken(value: unknown): value is SalesTasksOrderToken {
  return typeof value === "string" && Object.hasOwn(TASKS_ORDER_SET, value);
}

type TasksViewFilters = components["schemas"]["SavedViewTasksConfig"]["filters"];

/** The `due` windows (D-313 ruling 3): `upcoming` is after the tenant's today
 * and at most seven days ahead; `through_today` is My day's. */
export const TASK_DUE_TOKENS = [
  "overdue",
  "today",
  "through_today",
  "upcoming",
] as const satisfies readonly NonNullable<TasksViewFilters["due"]>[];

/** The statuses the address holds. An absent status is open. */
export const TASK_CLOSED_STATUSES = ["done", "cancelled"] as const satisfies readonly NonNullable<
  TasksViewFilters["status"]
>[];

export interface SalesTasksSearch {
  readonly view?: string | undefined;
  /** `all`, sent as `scope=all`; absent is the reader's own tasks. */
  readonly scope?: NonNullable<TasksViewFilters["scope"]> | undefined;
  /** `done` or `cancelled`; absent is open. */
  readonly status?: NonNullable<TasksViewFilters["status"]> | undefined;
  readonly due?: NonNullable<TasksViewFilters["due"]> | undefined;
  /** The customer, sent as `customer_id`. */
  readonly customer?: string | undefined;
  /** The assignee, sent as `assignee_user_id`. */
  readonly assignee?: string | undefined;
  readonly order?: SalesTasksOrderToken | undefined;
  /** The side peek, `<kind>:<uuid>`; never a filter. */
  readonly peek?: string | undefined;
}

// The screen's URL filter keys, pinned to its view config.
const TASKS_FILTER_KEYS: FilterKeyPin<SalesTasksSearch, TasksViewFilters> = {
  scope: true,
  status: true,
  due: true,
  customer: true,
  assignee: true,
};
export const SALES_TASKS_FILTER_KEYS = Object.keys(
  TASKS_FILTER_KEYS,
) as readonly (keyof TasksViewFilters)[];

/**
 * My day's id (spec 09 §4.16, D-313 ruling 8, D-314 ruling 7): the system view
 * on `sales.tasks` that migration 0073 writes, `due: through_today` over the
 * reader's own open tasks. The office home's "Open My day" links to
 * `/sales/tasks?view=<id>`. Restated from `SYSTEM_SAVED_VIEW_MY_DAY_ID` in
 * packages/contracts, because the contracts root is kept out of the office
 * bundle; `tasks-search-params.test.ts` holds the two equal.
 */
export const MY_DAY_VIEW_ID = "019f1af9-b400-7000-8000-000000000004";

/** The task-list address that opens My day. */
export function myDaySearch(): SalesTasksSearch {
  return { view: MY_DAY_VIEW_ID };
}

export function validateSalesTasksSearch(search: Record<string, unknown>): SalesTasksSearch {
  const order = search["order"];
  return {
    view: viewIdParam(search["view"]),
    scope: search["scope"] === "all" ? "all" : undefined,
    status: oneOf(TASK_CLOSED_STATUSES, search["status"]),
    due: oneOf(TASK_DUE_TOKENS, search["due"]),
    customer: uuidParam(search["customer"]),
    assignee: uuidParam(search["assignee"]),
    order: isTasksOrderToken(order) ? order : undefined,
    peek: peekSearchParam(search["peek"]),
  };
}
